/**
 * Application autonome : navigation latérale adaptée aux capabilities,
 * routeur par hash, indicateur hors ligne et file d'attente (§ 11).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api, bus, isLoggedIn, logout, flushQueue, queueLength, hydraterJetons } from './lib/api.js';
import Login, { ChangerMotDePasse } from './views/Login.jsx';
import { MesActivites, MesGroupes, MesInscriptions, Preferences } from './views/MemberViews.jsx';
import { Activites, ActiviteDetail, ActiviteCreer } from './views/ActivitesViews.jsx';
import { MoniteurCalendrier, MoniteurGroupe, MoniteurPresences } from './views/MoniteurViews.jsx';
import { Clubhouse, ClubhouseValidation } from './views/ClubhouseViews.jsx';
import { Admissions } from './views/AdmissionsViews.jsx';
import { Demandes, ListeAnimations, ListeInitiations, MesAnimations } from './views/PublicViews.jsx';
import { Actualites, ContactsComite, ClubhouseMembre, MesReservations } from './views/ExtrasViews.jsx';
import { Spinner, Toast, peut, groupesDe, IconeCalendrier, IconeMaison, IconeCible } from './components/common.jsx';

const NAV = [
  { path: 'calendrier', label: 'Mes Activités' },
  { section: 'Personnel' },
  { path: 'mes-reservations', label: 'Mes réservations' },
  { path: 'inscriptions', label: 'Mes inscriptions' },
  { path: 'groupes', label: 'Mes entraînements' },
  { path: 'mes-animations', label: 'Mes Animations' },
  { section: 'Membre' },
  { path: 'locations', label: 'Locations', cap: 'adlt_request_clubhouse' },
  { path: 'activites', label: 'Activités' },
  { path: 'animations', label: 'Animations' },
  { path: 'actualites', label: 'Actualités' },
  { path: 'contacts', label: 'Comité' },
  { section: 'Moniteur', cap: 'adlt_view_group_calendar' },
  { path: 'moniteur', label: 'Gestion des entraînements', cap: 'adlt_view_group_calendar' },
  { path: 'presences', label: 'Décomptes des présences', cap: 'adlt_declare_presence' },
  { path: 'initiations', label: 'Liste des cours débutants', cap: 'adlt_view_group_calendar' },
  { section: 'Comité', cap: 'adlt_validate_clubhouse' },
  { path: 'gestion-animations', label: 'Gestion des animations', cap: 'adlt_manage_demandes' },
  { path: 'gestion-initiations', label: 'Gestion des cours débutants', cap: 'adlt_manage_demandes' },
  { path: 'clubhouse-validation', label: 'Gestion des réservations', cap: 'adlt_validate_clubhouse' },
  { path: 'admissions', label: 'Gestion des admissions', cap: 'adlt_manage_admissions' },
  { section: '' },
  { path: 'preferences', label: 'Préférences' },
];

/**
 * Barre d'accès rapide en pied d'application : Activités, Club-house et
 * les entraînements du membre (les moniteurs gardent la Gestion en sidebar).
 */
const FOOTER = [
  { path: 'activites', label: 'Activités', Icone: IconeCalendrier },
  { path: 'locations', label: 'Club-house', Icone: IconeMaison, cap: 'adlt_request_clubhouse' },
  { path: 'groupes', label: 'Entraînements', Icone: IconeCible },
];

function FooterNav({ me, route }) {
  const items = FOOTER.filter((i) => peut(me, i.cap));
  if (!items.length) return null;
  return (
    <nav className="adlt-footer" aria-label="Accès rapide">
      {items.map(({ path, label, Icone }) => (
        <a key={path} href={'#/' + path}
          className={`adlt-footer-link ${route === path ? 'active' : ''}`}
          aria-current={route === path ? 'page' : undefined}>
          <Icone />
          <span>{label}</span>
        </a>
      ))}
    </nav>
  );
}

function useHash() {
  const [hash, setHash] = useState(location.hash.replace(/^#\/?/, '') || 'calendrier');
  useEffect(() => {
    const fn = () => setHash(location.hash.replace(/^#\/?/, '') || 'calendrier');
    window.addEventListener('hashchange', fn);
    return () => window.removeEventListener('hashchange', fn);
  }, []);
  return hash;
}

export default function App() {
  const [me, setMe] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(0);
  const [toast, setToast] = useState(null);
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [lieux, setLieux] = useState([]);
  const hash = useHash();

  const refreshMe = useCallback(async () => {
    const r = await api('me', { cache: 'me' });
    if (r.ok) setMe(r.data);
    setChargement(false);
  }, []);

  useEffect(() => {
    hydraterJetons().then(() => {
      if (isLoggedIn()) { refreshMe(); flushQueue(); } else setChargement(false);
      api('lieux', { cache: 'lieux' }).then((r) => r.ok && setLieux(r.data));
    });
    const offs = [
      bus.on('online', setOnline),
      bus.on('logout', () => setMe(null)),
      bus.on('queue-changed', async () => setQueue(await queueLength())),
      bus.on('queue-flushed', ({ done, failed }) => {
        if (failed.length) {
          setToast({ message: `⚠ ${failed.length} action(s) refusée(s) : ` + failed.map((f) => `${f.label} (${f.error})`).join(' · '), error: true });
        } else if (done) {
          setToast({ message: `${done} action(s) en attente transmise(s).` });
        }
      }),
    ];
    queueLength().then(setQueue);
    return () => offs.forEach((off) => off());
  }, [refreshMe]);

  // `id` croissant : deux messages identiques restent deux toasts distincts.
  const notifier = useCallback((message, error) => setToast({ message, error, id: Date.now() }), []);

  if (chargement) return <Spinner />;
  if (!me) return <Login onLoggedIn={(profil) => { setMe(profil); refreshMe(); }} />;
  // Compte importé : le mot de passe provisoire (date de naissance) doit être
  // remplacé avant tout accès au contenu.
  if (me.doit_changer_mdp) return <ChangerMotDePasse me={me} onDone={refreshMe} />;

  const can = (cap) => peut(me, cap);
  const [route, param] = hash.split('/');

  let vue;
  const openActivite = (id) => { location.hash = '#/activite/' + id; };
  switch (route) {
    case 'groupes': vue = <MesGroupes me={me} />; break;
    case 'activites': vue = <Activites me={me} toast={notifier} onOpen={(id) => { location.hash = id === 'creer' ? '#/activite-creer' : '#/activite/' + id; }} />; break;
    case 'activite': vue = <ActiviteDetail id={parseInt(param, 10)} me={me} toast={notifier} onBack={() => { location.hash = '#/activites'; }} />; break;
    case 'activite-creer': vue = <ActiviteCreer me={me} toast={notifier} groupes={groupesDe(me)} onDone={openActivite} />; break;
    case 'inscriptions': vue = <MesInscriptions onOpenActivite={openActivite} />; break;
    case 'locations': case 'clubhouse': vue = <ClubhouseMembre me={me} toast={notifier} />; break;
    case 'mes-reservations': vue = <MesReservations toast={notifier} />; break;
    case 'occupation': vue = <Clubhouse me={me} toast={notifier} />; break;
    case 'actualites': vue = <Actualites />; break;
    case 'contacts': vue = <ContactsComite />; break;
    case 'animations': vue = <ListeAnimations onOpenActivite={openActivite} />; break;
    case 'mes-animations': vue = <MesAnimations onOpenActivite={openActivite} />; break;
    case 'initiations': vue = <ListeInitiations onOpenActivite={openActivite} />; break;
    case 'gestion-animations': vue = <Demandes toast={notifier} type="animation" />; break;
    case 'gestion-initiations': vue = <Demandes toast={notifier} type="initiation" />; break;
    case 'demandes': vue = <Demandes toast={notifier} />; break;
    case 'moniteur': vue = <MoniteurCalendrier me={me} toast={notifier} lieux={lieux} />; break;
    case 'moniteur-groupe': vue = <MoniteurGroupe groupeId={parseInt(param, 10)} me={me} toast={notifier} onBack={() => { location.hash = '#/groupes'; }} />; break;
    case 'presences': vue = <MoniteurPresences me={me} />; break;
    case 'clubhouse-validation': vue = <ClubhouseValidation toast={notifier} />; break;
    case 'admissions': vue = <Admissions toast={notifier} />; break;
    case 'preferences': vue = <Preferences me={me} refreshMe={refreshMe} />; break;
    default: vue = <MesActivites me={me} toast={notifier} onEventClick={(ev) => { if (ev.type === 'activite') openActivite(ev.activite_id); }} />;
  }

  return (
    <div className="adlt-app d-flex">
      <aside className={`adlt-sidebar ${menuOuvert ? 'open' : ''}`}>
        <div className="p-3 d-flex align-items-center gap-2 border-bottom border-light border-opacity-25">
          {(window.ADLT_CONFIG || {}).logo
            ? <img src={window.ADLT_CONFIG.logo} alt="" style={{ height: 44, maxWidth: '100%', objectFit: 'contain' }} />
            : <strong className="text-white">🏹 Archers de la Tour</strong>}
        </div>
        <nav className="flex-grow-1 overflow-auto py-2">
          {NAV.filter((item) => can(item.cap)).map((item, i) => item.section !== undefined ? (
            item.section ? <div key={i} className="adlt-nav-section">{item.section}</div> : <hr key={i} className="border-light border-opacity-25 my-2" />
          ) : (
            <a key={item.path} href={'#/' + item.path}
              className={`adlt-nav-link ${route === item.path ? 'active' : ''}`}
              onClick={() => setMenuOuvert(false)}>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="p-3 border-top border-light border-opacity-25">
          <div className={`small mb-2 ${online ? 'text-success' : 'text-warning'}`}>
            ● {online ? 'En ligne' : 'Hors ligne'}{queue > 0 && ` · ${queue} en attente`}
          </div>
          <div className="text-white-50 small mb-2">{me.nom}</div>
          <button className="btn btn-outline-light btn-sm w-100" onClick={() => logout()}>Se déconnecter</button>
        </div>
      </aside>
      {menuOuvert && <div className="adlt-scrim" onClick={() => setMenuOuvert(false)}></div>}
      <main className="adlt-main flex-grow-1">
        <div className="adlt-topbar d-lg-none">
          <button className="btn btn-link text-white fs-4 p-1" onClick={() => setMenuOuvert(true)} aria-label="Menu">☰</button>
          <strong>Archers de la Tour</strong>
        </div>
        {!online && <div className="alert alert-warning py-2 mb-3">📡 Hors ligne — données du cache, certaines actions seront différées.</div>}
        <div className="container-fluid px-0">{vue}</div>
        <FooterNav me={me} route={route} />
      </main>
      {toast && <Toast message={toast.message} error={toast.error} id={toast.id} onDone={() => setToast(null)} />}
    </div>
  );
}
